import { computed, ref, watch } from 'vue';
import { acceptHMRUpdate, defineStore, storeToRefs } from 'pinia';
import { isNumber, keys, pick, reduce, values, uniq } from 'lodash-es';
import { useTariffsStore } from '../tariffs';
import { tariffsService } from '@/services/tariffs';
import { useApiResponsesStore } from '../responses';
import { getWorkingDaysPerYear } from '@/utils/calculations';

/**
 * This store contains all form related data
 */
export const usePersonalizedTariffFormStore = defineStore(
    'personalized-tariff-form',
    () => {
        const tariffsStore = useTariffsStore();
        const { yearly, weekly, monthly, hourly } = storeToRefs(tariffsStore);

        const apiResponseStore = useApiResponsesStore();
        const { countryResponses } = storeToRefs(apiResponseStore);

        const formCostFields = {
            computerMonthlyCosts: null,
            phoneMonthlyCosts: null,
            carMonthlyCosts: null,
            scooterMonthlyCosts: null,
            electricBicycleMonthlyCosts: null,
            bicycleMonthlyCosts: null,
            miscMonthlyCosts: null,
        };
        const formJobRelatedTimeFields = {
            administrationHoursPerWeek: null,
            acquisitionHoursPerWeek: null,
            trainingHoursPerWeek: null,
            waitingHoursPerWeek: null,
        };
        const socialContributionFields = {
            pension__Contribution: null,
            sickness__Contribution: null,
            disability__Contribution: null,
            no_work_available__Contribution: null,
            medical_insurance__Contribution: null,
        };
        const formData = ref({
            publicHolidaysPerYear: null,
            leaveDaysPerYear: null,
            workingDaysPerWeek: null,
            workingHoursPerWeek: null,
            workingDaysPerYear: null,
            ...formCostFields,
            ...formJobRelatedTimeFields,
            ...socialContributionFields,
        });

        const yearlyWorkTimeMinusDaysOff = ref({
            days: null,
            hours: null,
        })

        const costDifferences = ref({
            computer: null,
            phone: null,
            car: null,
            scooter: null,
            electricBicycle: null,
            bicycle: null,
        });
        const jobRelatedTimeDifferences = ref({
            administration: null,
            acquisition: null,
            training: null,
            waiting: null,
        });
        const socialSecurityTaxesResponse = ref({});

        const _sum = (valueA, valueB) => {
            return valueA + valueB;
        };

        const _diffObject = (original, input) => {
            return {
                original: original,
                input: input,
            }
        };

        const _setDifference = (inputValue, inputType, diffObj, tariffObj) => {
            const value = parseFloat(inputValue);

            if (value === 0 || value && isNumber(value)) {
                diffObj.value[inputType] =
                    parseFloat(tariffObj[inputType]) - inputValue;
            } else {
                diffObj.value[inputType] = null;
            }
        };

        const _getTotalAmount = (object, fields) => {
            const items = values(pick(object, fields));

            const uniqItems = uniq(items);
            if (uniqItems.length  === 1 && uniqItems[0] === null ) {
                return null;
            }

            return reduce(items, _sum);
        };

        const _getWorkingHoursPerMonth = () => {
            if (formData.value.workingHoursPerWeek) {
                return (yearlyWorkTimeMinusDaysOff.value.days * ( formData.value.workingHoursPerWeek /  formData.value.workingDaysPerWeek)) / 12;
            }
        };


        const overheadGrandTotal =  computed(() => {
            let jobCosts = {};
            let jobCostsDiff = {};

            if (_getWorkingHoursPerMonth()) {
                jobCosts = {
                    hourly: null,
                    monthly: _getTotalAmount(formData.value, keys(formCostFields)),
                    yearly: null,
                };

                if (jobCosts.monthly !== null) {
                    jobCosts.yearly = jobCosts.monthly * 12;
                    jobCosts.hourly = jobCosts.monthly / _getWorkingHoursPerMonth();
                }

                jobCostsDiff = {
                    hourly: null,
                    monthly:  _getTotalAmount(costDifferences.value, keys(costDifferences.value)),
                    yearly: null,
                };

                if (jobCostsDiff.monthly) {
                    jobCostsDiff.yearly = yearly.value.jobRelatedCosts.total - jobCosts.yearly;
                    jobCostsDiff.hourly = hourly.value.jobRelatedCosts.total - jobCosts.hourly;
                }
            }

            const jobRelatedTime = {
                weekly: _getTotalAmount(formData.value, keys(formJobRelatedTimeFields)),
            };
            const jobRelatedTimeDiff = {
                weekly: _getTotalAmount( jobRelatedTimeDifferences.value, keys(jobRelatedTimeDifferences.value)),
            };

            return {
                jobCosts,
                jobCostsDiff,
                jobRelatedTime,
                jobRelatedTimeDiff,
            }
        });

        const livingWages = computed(() => {
            let hourlyWithoutOverhead = null;
            // let monthlyWithoutOverhead = null;

            let hourlyWithCostsAndOverhead = null;
            let monthlyWithCostsAndOverhead = null;
            let yearlyWithCostsAndOverhead = null;

            let hourlyWithTaxes = null;
            let monthlyWithTaxes = null;
            let yearlyWithTaxes = null;

            if (formData.value.workingDaysPerWeek) {
                formData.value.workingDaysPerYear = getWorkingDaysPerYear(
                    yearly.value.current,
                    formData.value.workingDaysPerWeek
                );
            }

            if (
                isNumber(formData.value.publicHolidaysPerYear) &&
                isNumber(formData.value.leaveDaysPerYear) &&
                formData.value.workingDaysPerWeek &&
                formData.value.workingHoursPerWeek
            ) {
                yearlyWorkTimeMinusDaysOff.value.days =
                    formData.value.workingDaysPerYear -
                    formData.value.publicHolidaysPerYear -
                    formData.value.leaveDaysPerYear;
                const dailyWorkingHours =
                    formData.value.workingHoursPerWeek /
                    formData.value.workingDaysPerWeek;

                yearlyWorkTimeMinusDaysOff.value.hours =
                    yearlyWorkTimeMinusDaysOff.value.days * dailyWorkingHours;

                hourlyWithoutOverhead =
                    yearly.value.livingWages.net / yearlyWorkTimeMinusDaysOff.value.hours;
                // monthlyWithoutOverhead = monthly.value.livingWages.net;

                yearlyWithCostsAndOverhead = yearly.value.livingWages.net + overheadGrandTotal.value.jobCosts.yearly;
                monthlyWithCostsAndOverhead = yearlyWithCostsAndOverhead / 12;

                const hourlyOverhead =
                    (overheadGrandTotal.value.jobRelatedTime.weekly * 52.2 / yearly.value.fullTimeWorkingHours ) *
                    (hourly.value.livingWages.withoutOverhead +
                        hourly.value.jobRelatedCosts.total);

                hourlyWithCostsAndOverhead = (monthlyWithCostsAndOverhead / _getWorkingHoursPerMonth()) + hourlyOverhead;

                if (isNumber(socialSecurityTaxes.value.monthly.total)) {
                    monthlyWithTaxes =
                        monthlyWithCostsAndOverhead +
                        socialSecurityTaxes.value.monthly.total;

                    hourlyWithTaxes = hourlyWithCostsAndOverhead + socialSecurityTaxes.value.hourly.total;

                    yearlyWithTaxes = monthlyWithTaxes * 12;
                }
            }

            return {
                withoutOverhead: {
                    hourly: hourlyWithoutOverhead,
                },
                withCostsAndOverhead: {
                    monthly: monthlyWithCostsAndOverhead,
                    yearly: yearlyWithCostsAndOverhead,
                    hourly: hourlyWithCostsAndOverhead,
                },
                // TODO: rename to withIncomeTax?
                withTaxes: {
                    hourly: hourlyWithTaxes,
                    monthly: monthlyWithTaxes,
                    yearly: yearlyWithTaxes,
                },
            };
        });

        const livingWageDifferences = computed(() => {
            const hourlyWithoutOverhead =
                livingWages.value.withoutOverhead.hourly;

            const withCostsAndOverhead =
                livingWages.value.withCostsAndOverhead.monthly;

            const hourlyWithTaxes = livingWages.value.withTaxes.hourly;
            const monthlyWithTaxes = livingWages.value.withTaxes.monthly;
            const yearlyWithTaxes = livingWages.value.withTaxes.yearly;

            const values = {
                withoutOverhead: {
                    hourly: _diffObject(
                        hourly.value.livingWages.withoutOverhead,
                        hourlyWithoutOverhead,
                    ),
                },
                withCostsAndOverhead: {
                    hourly: _diffObject(
                        hourly.value.livingWages.withCostsAndOverhead,
                        null,
                        ),
                    monthly: _diffObject(
                        monthly.value.livingWages.withCostsAndOverhead,
                        withCostsAndOverhead,
                    ),
                    yearly: _diffObject(
                        yearly.value.livingWages.withCostsAndOverhead,
                        null,
                    ),

                    _monthly: withCostsAndOverhead
                        ? monthly.value.livingWages.withCostsAndOverhead -
                          withCostsAndOverhead
                        : null,
                },
                withTaxes: {
                    hourly: hourlyWithTaxes
                        ? hourly.value.livingWages.withIncomeTax -
                          hourlyWithTaxes
                        : null,
                    monthly: monthlyWithTaxes
                        ? monthly.value.livingWages.withIncomeTax -
                          monthlyWithTaxes
                        : null,
                    yearly: yearlyWithTaxes
                        ? yearly.value.livingWages.withIncomeTax -
                          yearlyWithTaxes
                        : null,
                },
            };
            if (values.withCostsAndOverhead.monthly.input && _getWorkingHoursPerMonth()) {
                values.withCostsAndOverhead.yearly.input = values.withCostsAndOverhead.monthly.input * 12;
                values.withCostsAndOverhead.hourly.input = livingWages.value.withCostsAndOverhead.hourly;
            }
            return values;
        });

        const socialSecurityTaxes = computed(() => {
            let totalMonthly = 0;

            let monthlyIncomeTax;
            const yearlyIncomeTax =
                socialSecurityTaxesResponse.value['income_tax'];
            if (isNumber(yearlyIncomeTax)) {
                monthlyIncomeTax = yearlyIncomeTax / 12;
                totalMonthly += monthlyIncomeTax;
            }
            const contributions = {};
            keys(socialContributionFields).forEach(
                (key) => {
                    const formValue = formData.value[key];
                    if (formValue) {
                        totalMonthly += formValue;
                    }
                }
            );

            const values = {
                monthly: {
                    incomeTax: monthlyIncomeTax,
                    contributions: contributions,
                },
                yearly: {
                },
                hourly: {
                },
            };

            if (_getWorkingHoursPerMonth()) {
                values.monthly.total = totalMonthly;
                values.yearly.total = totalMonthly * 12;
                values.hourly.total = totalMonthly / _getWorkingHoursPerMonth();
            }
            return values;
        });

        // Watch form data for changes to calculate differences related to cost and job related time
        watch([formData.value], ([formData]) => {
            keys(costDifferences.value).forEach((costType) => {
                const formValue = formData[`${costType}MonthlyCosts`];
                _setDifference(
                    formValue,
                    costType,
                    costDifferences,
                    monthly.value.jobRelatedCosts
                );
            });
            keys(jobRelatedTimeDifferences.value).forEach((timeType) => {
                const formValue = formData[`${timeType}HoursPerWeek`];
                _setDifference(
                    formValue,
                    timeType,
                    jobRelatedTimeDifferences,
                    weekly.value.jobRelatedHours
                );
            });
        });

        // Watch living wage with cost + overhead to fetch matching income tax and social contributions
        watch(
            () => livingWages.value.withCostsAndOverhead.yearly,
            (value) => {
                if (value) {
                    const intValue = Math.trunc(parseInt(value));
                    tariffsService
                        .socialSecurityTaxes(
                            countryResponses.value.numericCode,
                            intValue
                        )
                        .then((response) => {
                            socialSecurityTaxesResponse.value = response;
                        });
                } else {
                    socialSecurityTaxesResponse.value = {};
                }
            }
        );

        return {
            formData,
            yearlyWorkTimeMinusDaysOff,
            livingWages,
            livingWageDifferences,
            socialSecurityTaxes,
            overheadGrandTotal,
        };
    }
);

if (import.meta.hot) {
    import.meta.hot.accept(
        acceptHMRUpdate(usePersonalizedTariffFormStore, import.meta.hot)
    );
}
